import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertCircle,
  Clock,
  Compass,
  Loader2,
  Pause,
  Play,
  RotateCcw,
  WifiOff,
  X,
} from 'lucide-react';
import { cn } from '../lib/cn';
import type { AttachmentRecord } from '../lib/media';
import type { MessageKind } from '../lib/storage';
export interface ChatMessage {
  id: string;
  /**
   * Message origin. 'persona' = the town's Insider (Morwenna, Jago, Demelza)
   * or, when partner-attributed, the partner persona (Ollie at Harlyn Sands).
   * The bubble doesn't know which persona by name — visual identity comes
   * from the surrounding header context (which is partner-aware) and from
   * the `partnerPrimary` prop on ChatBubble (which carries the partner
   * brand colour when attributed).
   */
  from: 'persona' | 'user';
  text: string;
  timestamp: string;
  /**
   * Local calendar date YYYY-MM-DD when the message was created. Used for
   * grouping history by day in downstream features (Memory Page timeline,
   * analytics). Always present; persisted alongside the message. NOT a
   * UTC slice — see `isoDateLocal` in onboarding.ts.
   */
  dateLocal: string;
  /**
   * Provenance / intent of the message. Set at construction site, returned
   * from the (mock) sendMessage response for persona replies, persisted
   * alongside the message. See MessageKind in storage.ts.
   */
  kind: MessageKind;
  /**
   * Venue name — populated only on `kind === 'pick-ack'` messages by
   * sendMessage's pick-ack path. Lets Memory Page enrichment recover
   * "places you went" without parsing the persona's voiced ack text
   * (which would be fragile to per-persona phrasing tweaks).
   */
  pickName?: string;
  attachments?: AttachmentRecord[];
  status?: 'sending' | 'sent' | 'error';
  uploadProgress?: number; // 0–100
  /**
   * Outbox lifecycle status for user messages routed through the chat
   * send outbox (Batch 1d). When set, takes precedence over the legacy
   * `status` field for badge rendering — `status` continues to govern
   * attachment-upload progress for non-outbox-driven messages
   * (attachment-only sends, onboarding bubbles, persona placeholders).
   *
   * Four states:
   *   - 'queued'             waiting for connectivity / mutex; no
   *                          attempt in flight yet.
   *   - 'sending'            drain attempt in flight; bubble shows
   *                          spinner identical to the legacy upload
   *                          'sending' but driven from the outbox.
   *   - 'failed-retryable'   attempt failed with a code the outbox
   *                          treats as worth another try (network,
   *                          5xx, persona_overloaded, persistence,
   *                          tool_loop). Retry button renders when
   *                          `onRetry` is supplied.
   *   - 'failed-permanent'   attempt failed with a code that won't
   *                          succeed on retry (validation_error,
   *                          stay_ended, unauthorized, forbidden).
   *   Dismiss button renders when `onDismiss` is supplied.
   *
   * Persona messages never carry this — outbox is unidirectional
   * client→server.
   *
   * Not persisted to localStorage: storage.ts strips message status
   * to 'sent' on read, so a reload picks up the outbox state from
   * the outbox itself (src/lib/outbox.ts) and MePage re-hydrates
   * the bubble's outboxStatus from there. Decoupling means a stuck
   * outbox item can't poison the persisted chat history.
   */
  outboxStatus?:
    | 'queued'
    | 'sending'
    | 'failed-retryable'
    | 'failed-permanent';
  /**
   * Outbox item id (Batch 1d). The persisted-outbox key
   * for this message — required for the retry / dismiss callbacks
   * to identify which queue entry they're acting on. Without this
   * the retry / dismiss buttons stay hidden even if the callbacks
   * are wired in (the bubble has no handle to mutate via).
   */
  outboxItemId?: string;
}
interface ChatBubbleProps {
  message: ChatMessage;
  /**
   * Partner primary hex (Batch 12). Present when the chat session is
   * partner-attributed AND the partner has supplied a brand colour;
   * undefined for town-only sessions. When present:
   *   - User bubble bg picks up partnerPrimary (overrides bg-moor-700
   *     via inline style; class-based hover stays inert because the
   *     bubble doesn't have a hover state).
   *   - Persona avatar (the small compass next to each Ollie bubble)
   *     picks up partnerPrimary, matching the MePage header compass
   *     for visual coherence.
   *
   * Passed as a prop rather than read from `usePartner()` inside the
   * component to keep ChatBubble a leaf with no partner-data hook
   * dependencies — single owner (MePage) for the lookup, explicit
   * data flow, no per-bubble query subscription overhead.
   *
   * Optional + explicitly nullable to satisfy exactOptionalPropertyTypes
   * when the caller passes a `string | undefined` value through.
   */
  partnerPrimary?: string | undefined;
  /**
   * Retry handler for failed-retryable outbox items (Batch 1d).
   * Invoked with the message's `outboxItemId` when the user taps
   * the retry button on a failed-retryable badge. The caller decides
   * what to do — MePage will mark the item back to 'queued' and let
   * the drain loop pick it up.
   *
   * Optional. When omitted (or when `message.outboxItemId` is unset),
   * the retry button is suppressed; the failure badge still renders
   * so the user can still see the state. Lets non-MePage callers
   * (read-only history views, memory page) render outbox-tagged
   * messages without offering retry affordances.
   *
   * Explicit `| undefined` for exactOptionalPropertyTypes — MePage
   * sometimes passes the value through a ternary that may evaluate
   * to undefined.
   */
  onRetry?: ((outboxItemId: string) => void) | undefined;
  /**
   * Dismiss handler for failed-permanent outbox items (Batch 1d).
   * Invoked with the message's `outboxItemId` when the user taps the
   * dismiss button on a failed-permanent badge. MePage removes the
   * item from the queue; the bubble persists in chat history as a
   * normal user message (the failure marker is the only thing that
   * disappears).
   *
   * Optional + nullable for the same reasons as `onRetry` above.
   */
  onDismiss?: ((outboxItemId: string) => void) | undefined;
}
/**
 * Matches http/https URLs in free text. Deliberately narrow — only the
 * http and https schemes are linkified, so javascript:, data:, file:
 * and similar URIs can never be turned into anchors. Trailing prose
 * punctuation is trimmed off the match in renderTextWithLinks rather
 * than baked into this pattern.
 */
const URL_REGEX = /\bhttps?:\/\/[^\s<>()]+/gi;
/** Punctuation that often trails a URL in a sentence but isn't part of it. */
const TRAILING_PUNCT = /[.,;:!?'")\]}]+$/;
/**
 * Render message text with bare URLs turned into real, safe anchors.
 * Non-URL spans are returned as plain strings — the text is never passed
 * through any HTML interpreter (no dangerouslySetInnerHTML, no markdown
 * parser), so this adds no injection surface. URL-free text returns a
 * single string and renders exactly as before.
 */
function renderTextWithLinks(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let linkKey = 0;
  for (const match of text.matchAll(URL_REGEX)) {
    const raw = match[0];
    const start = match.index ?? 0;
    // Trim trailing sentence punctuation back out of the link itself.
    const href = raw.replace(TRAILING_PUNCT, '') || raw;
    if (start > lastIndex) {
      nodes.push(text.slice(lastIndex, start));
    }
    nodes.push(
      <a
        key={`lnk-${linkKey++}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="underline underline-offset-2 break-all"
      >
        {href}
      </a>
    );
    // Any punctuation trimmed off the match goes back as plain text.
    if (href.length < raw.length) {
      nodes.push(raw.slice(href.length));
    }
    lastIndex = start + raw.length;
  }
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }
  return nodes;
}
export function ChatBubble({
  message,
  partnerPrimary,
  onRetry,
  onDismiss,
}: ChatBubbleProps) {
  const { t } = useTranslation('mePage');
  const isPersona = message.from === 'persona';
  const tone: 'light' | 'dark' = isPersona ? 'light' : 'dark';
  const hasText = message.text.trim().length > 0;
  const hasAttachments = !!message.attachments && message.attachments.length > 0;
  // Offline FAQ fallback variant (Phase 6 sub-commit 6b, 13 Jun 2026).
  // When handleSend in MePage detects an offline match against the
  // cached FAQ catalogue, it writes a bubble with this kind instead
  // of routing through the live persona path. Visually distinguished
  // so a guest can never confuse a cached answer for the live persona
  // speaking: dashed border replaces the standard ring, and a
  // "Saved info" pill with a WifiOff icon sits in the meta row.
  // Premium discipline: honest labelling > seamless illusion.
  const isOfflineFaq = message.kind === 'persona-offline-faq';
  // User bubble: bg-moor-700 is the no-partner default; when
  // partnerPrimary is supplied, the inline style below overrides it
  // (inline style outranks any class-based bg via CSS specificity).
  // Persona bubble: stays cream-100 regardless — warm light bubble
  // reads cleanly on both cream and white page backgrounds, and
  // partner branding lives on the user bubble + avatar, not the
  // persona's own speech bubble.
  //
  // Persona / offline-faq distinction: same bg (cream-100), but the
  // ring becomes a dashed moor-300 border. Dashed = common UX
  // vocabulary for "cached / saved / not live", and the moor-300
  // tone keeps it calm rather than alarming (amber/red would imply
  // error or warning, which an offline cached answer is not).
  const bubbleCls = isPersona
    ? isOfflineFaq
      ? 'rounded-tl-sm bg-cream-100 text-moor-900 border border-dashed border-moor-300'
      : 'rounded-tl-sm bg-cream-100 text-moor-900 ring-1 ring-moor-100'
    : `rounded-tr-sm text-cream-50 ${partnerPrimary ? '' : 'bg-moor-700'}`;
  const metaCls = isPersona ? 'text-moor-500' : 'text-cream-200/80';
  const userBubbleStyle =
    !isPersona && partnerPrimary
      ? { backgroundColor: partnerPrimary }
      : undefined;
  // Outbox state takes precedence over the legacy `status` field for
  // badge rendering. Extracted up here so the JSX conditions stay flat
  // and readable. Capturing `outboxItemId` separately keeps the JSX
  // narrowing clean — TS otherwise won't track the non-undefined
  // narrowing through the boolean `&&` chain inside the closure.
  const outboxStatus = message.outboxStatus;
  const outboxItemId = message.outboxItemId;
  return (
    <div className={cn('flex w-full gap-2', isPersona ? 'justify-start' : 'justify-end')}>
      {isPersona && (
        <div
          aria-hidden="true"
          className={cn(
            'mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-cream-50',
            partnerPrimary ? '' : 'bg-moor-700'
          )}
          style={
            partnerPrimary ? { backgroundColor: partnerPrimary } : undefined
          }
        >
          <Compass className="h-4 w-4" />
        </div>
      )}
      <div
        className={cn(
          'max-w-[85%] overflow-hidden rounded-2xl text-[15px] leading-relaxed shadow-sm',
          bubbleCls
        )}
        style={userBubbleStyle}
      >
        {hasAttachments && (
          <div className="flex flex-col">
            {message.attachments!.map((att) => (
              <Attachment key={att.id} record={att} tone={tone} />
            ))}
          </div>
        )}
        {hasText && (
          <div
            className={cn(
              'whitespace-pre-wrap break-words px-4 py-3',
              isPersona && 'prose-local'
            )}
          >
            {renderTextWithLinks(message.text)}
          </div>
        )}
        <div
          className={cn(
            // `flex-wrap` so the retry / dismiss button can wrap to a
            // second row on narrow viewports (320–360px Android) when
            // it would otherwise extend past the 85%-max bubble width.
            // `gap-y-1` keeps wrapped rows from crowding the timestamp
            // visually. `gap-x-2` is slightly more breathing room
            // than the previous `gap-1.5` to compensate for the new
            // button affordance sharing the row.
            'flex flex-wrap items-center gap-x-2 gap-y-1 px-4 pb-2 text-xs',
            !hasText && hasAttachments && 'pt-2',
            !hasText && !hasAttachments && 'pt-2',
            metaCls
          )}
        >
          <span>{message.timestamp}</span>
          {/* Saved-info pill (Phase 6 sub-commit 6b, 13 Jun 2026).
              Renders ONLY for the offline-FAQ variant. Sits right
              after the timestamp so it reads as part of the bubble's
              provenance metadata, not as a status badge. WifiOff
              icon reinforces the offline-cached nature; text-moor-600
              is a half-shade stronger than the metaCls default
              (text-moor-500) so the label registers without competing
              with the bubble body. */}
          {isOfflineFaq && (
            <span className="inline-flex items-center gap-1 text-moor-600">
              <WifiOff className="h-3 w-3" aria-hidden="true" />
              <span>{t('bubbleSavedInfo')}</span>
            </span>
          )}
          {/* Outbox-driven badges (Batch 1d). Render when outboxStatus
              is set; the legacy upload-status badges below short-circuit
              themselves with an `outboxStatus === undefined` guard so
              the two systems can never both render at once. */}
          {outboxStatus === 'queued' && (
            <span className="flex items-center gap-1" aria-live="polite">
              <Clock className="h-3 w-3" aria-hidden="true" />
              <span>{t('bubbleQueued')}</span>
            </span>
          )}
          {outboxStatus === 'sending' && (
            <span className="flex items-center gap-1" aria-live="polite">
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
              <span className="sr-only">Sending</span>
            </span>
          )}
          {outboxStatus === 'failed-retryable' && (
            <span
              className="flex items-center gap-1.5 text-amber-300"
              role="status"
            >
              <AlertCircle className="h-3 w-3" aria-hidden="true" />
              <span>{t('bubbleFailedRetryable')}</span>
              {/* Retry button — narrowed `outboxItemId !== undefined`
                  rather than truthy check, so an empty-string id (we
                  don't generate them but the type allows it) wouldn't
                  silently suppress the affordance. The `px-1.5 py-0.5`
                  gives a tap target slightly larger than the visual
                  text without forcing the row taller; on narrow
                  viewports the flex-wrap above lets the button drop
                  to its own row, where it gets its own line height. */}
              {onRetry && outboxItemId !== undefined && (
                <button
                  type="button"
                  onClick={() => onRetry(outboxItemId)}
                  className="ml-0.5 inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 underline underline-offset-2 hover:no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-cream-50/40"
                >
                  <RotateCcw className="h-3 w-3" aria-hidden="true" />
                  <span>{t('bubbleRetry')}</span>
                </button>
              )}
            </span>
          )}
          {outboxStatus === 'failed-permanent' && (
            <span
              className="flex items-center gap-1.5 text-red-300"
              role="status"
            >
              <AlertCircle className="h-3 w-3" aria-hidden="true" />
              <span>{t('bubbleFailedPermanent')}</span>
              {onDismiss && outboxItemId !== undefined && (
                <button
                  type="button"
                  onClick={() => onDismiss(outboxItemId)}
                  className="ml-0.5 inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 underline underline-offset-2 hover:no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-cream-50/40"
                >
                  <X className="h-3 w-3" aria-hidden="true" />
                  <span>{t('bubbleDismiss')}</span>
                </button>
              )}
            </span>
          )}
          {/* Legacy upload-status badges. Only render when no outbox
              status is set (i.e. non-outbox-driven messages: attachment
              uploads, persona placeholders during streaming). Hardcoded
              strings retained at this surface — separate i18n cleanup
              batch will move "Sending" sr-only / "Failed to send" /
              "Photo" / voice player labels in one pass. */}
          {outboxStatus === undefined && message.status === 'sending' && (
            <span className="flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
              <span className="sr-only">Sending</span>
              {typeof message.uploadProgress === 'number' && (
                <span className="tabular-nums" aria-live="polite">
                  {message.uploadProgress}%
                </span>
              )}
            </span>
          )}
          {outboxStatus === undefined && message.status === 'error' && (
            <span className="flex items-center gap-1 text-red-300" role="status">
              <AlertCircle className="h-3 w-3" aria-hidden="true" />
              <span>Failed to send</span>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
function Attachment({
  record,
  tone,
}: {
  record: AttachmentRecord;
  tone: 'light' | 'dark';
}) {
  if (record.kind === 'image') {
    return (
      <img
        src={record.url}
        alt="Photo"
        loading="lazy"
        decoding="async"
        width={record.width}
        height={record.height}
        className="block max-h-80 w-full object-cover"
      />
    );
  }
  return (
    <div className="px-3 pt-3 pb-1">
      <VoicePlayer url={record.url} durationMs={record.durationMs} tone={tone} />
    </div>
  );
}
function VoicePlayer({
  url,
  durationMs,
  tone,
}: {
  url: string;
  durationMs: number | undefined;
  tone: 'light' | 'dark';
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [position, setPosition] = useState(0); // 0–1
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime = () => {
      if (audio.duration > 0 && Number.isFinite(audio.duration)) {
        setPosition(audio.currentTime / audio.duration);
      }
    };
    const onEnded = () => {
      setIsPlaying(false);
      setPosition(0);
    };
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('ended', onEnded);
    return () => {
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('ended', onEnded);
    };
  }, []);
  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      void audio
        .play()
        .then(() => setIsPlaying(true))
        .catch(() => setIsPlaying(false));
    }
  };
  const seconds = durationMs ? Math.round(durationMs / 1000) : 0;
  const mm = Math.floor(seconds / 60);
  const ss = String(seconds % 60).padStart(2, '0');
  const buttonCls =
    tone === 'dark'
      ? 'bg-cream-50 text-moor-900 hover:bg-cream-100'
      : 'bg-moor-700 text-cream-50 hover:bg-moor-800';
  const trackCls = tone === 'dark' ? 'bg-cream-200/40' : 'bg-moor-200';
  const fillCls = tone === 'dark' ? 'bg-cream-50' : 'bg-moor-700';
  const timeCls = tone === 'dark' ? 'text-cream-200/80' : 'text-moor-600';
  return (
    <div className="flex min-w-[180px] items-center gap-3">
      <audio ref={audioRef} src={url} preload="metadata" />
      <button
        type="button"
        onClick={toggle}
        aria-label={isPlaying ? 'Pause voice note' : 'Play voice note'}
        className={cn(
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors',
          'focus:outline-none focus:ring-2 focus:ring-moor-500/40 focus:ring-offset-2',
          buttonCls
        )}
      >
        {isPlaying ? (
          <Pause className="h-4 w-4" aria-hidden="true" />
        ) : (
          <Play className="h-4 w-4 translate-x-[1px]" aria-hidden="true" />
        )}
      </button>
      <div className="flex flex-1 items-center gap-2">
        <div
          className={cn('h-1 flex-1 overflow-hidden rounded-full', trackCls)}
          role="progressbar"
          aria-valuenow={Math.round(position * 100)}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className={cn('h-full transition-[width] duration-100', fillCls)}
            style={{ width: `${Math.round(position * 100)}%` }}
          />
        </div>
        <span className={cn('font-mono text-xs tabular-nums', timeCls)}>
          {mm}:{ss}
        </span>
      </div>
    </div>
  );
}
